import { Link, useNavigate } from "react-router-dom";
import { MapPin, Gauge, Fuel, Settings2, BadgeCheck, User, Star, TrendingDown } from "lucide-react";
import { formatINR, imgUrl } from "../lib/api";
import FavoriteButton from "./FavoriteButton";
import ShareButton, { carShare } from "./ShareButton";

const cityPath = (s, c) => `/city/${encodeURIComponent(s)}/${encodeURIComponent(c)}`;

export const SellerBadge = ({ type, verified = true }) =>
  type === "dealer" ? (
    <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-black px-2 py-0.5 rounded-full" style={{ background: "var(--gold-grad)" }}>
      <BadgeCheck className="w-2.5 h-2.5" /> {verified ? "Dealer" : "Dealer Store"}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-[#E5C158] border border-[#D4AF37]/40 bg-black/60 px-2 py-0.5 rounded-full">
      <User className="w-2.5 h-2.5" /> Owner
    </span>
  );

export const FeaturedRibbon = () => (
  <div className="absolute top-0 left-0 w-24 h-24 overflow-hidden pointer-events-none z-10" data-testid="featured-ribbon">
    <span className="absolute top-[14px] -left-[30px] w-[120px] -rotate-45 text-center text-[9px] font-extrabold tracking-[0.2em] text-black py-1 shadow-lg flex items-center justify-center gap-1" style={{ background: "var(--gold-grad)" }}>
      <Star className="w-2.5 h-2.5 fill-black" />FEATURED
    </span>
  </div>
);

export default function CarCard({ car, index = 0 }) {
  const navigate = useNavigate();
  return (
    <Link
      to={`/cars/${car.id}`}
      data-testid={`car-card-${car.id}`}
      className={`group lux-card overflow-hidden block rise transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:border-[#D4AF37]/60 ${car.featured ? "border-[#D4AF37]/60 shadow-[0_0_24px_rgba(212,175,55,0.12)]" : ""}`}
      style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        {car.featured && <FeaturedRibbon />}
        <img src={imgUrl(car.images?.[0])} alt={`${car.brand} ${car.model}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        <FavoriteButton id={car.id} className="absolute top-2 right-2 w-7 h-7" size="w-3.5 h-3.5" />
        <ShareButton {...carShare(car)} testid={`car-card-share-${car.id}`} className="absolute top-2 right-11 w-7 h-7 rounded-full bg-black/60 backdrop-blur border border-white/10 hover:border-[#D4AF37] flex items-center justify-center text-white" />
        {car.status === "sold" && <span className="absolute bottom-2 right-2 text-[10px] font-bold bg-red-600 text-white px-1.5 py-0.5 rounded">SOLD</span>}
        {car.previous_price > car.price && car.status !== "sold" && (
          <span className="absolute bottom-2 right-2 text-[9px] font-bold uppercase bg-emerald-600 text-white px-1.5 py-0.5 rounded flex items-center gap-0.5" data-testid="price-drop-badge"><TrendingDown className="w-2.5 h-2.5" />Price drop</span>
        )}
        <div className="absolute bottom-2 left-3 flex items-baseline gap-1.5">
          <p className="font-display text-lg font-semibold gold-text leading-none" data-testid="car-card-price">{formatINR(car.price)}</p>
          {car.previous_price > car.price && <p className="text-[10px] text-gray-400 line-through">{formatINR(car.previous_price)}</p>}
        </div>
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-semibold text-white text-sm leading-tight truncate">{car.year} {car.brand} {car.model}</h3>
            <p className="text-[11px] text-gray-400 truncate">{car.variant || "\u00A0"}</p>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <SellerBadge type={car.seller_type} />
            {car.is_demo && <span className="text-[8px] uppercase tracking-wider text-gray-500 border border-white/10 px-1.5 rounded-full" data-testid="demo-badge">Demo</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-400">
          <span className="flex items-center gap-1"><Gauge className="w-3 h-3 text-[#D4AF37]" />{car.km_driven.toLocaleString("en-IN")} km</span>
          <span className="flex items-center gap-1"><Fuel className="w-3 h-3 text-[#D4AF37]" />{car.fuel}</span>
          <span className="flex items-center gap-1"><Settings2 className="w-3 h-3 text-[#D4AF37]" />{car.transmission}</span>
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-white/5 text-[11px]">
          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(cityPath(car.state, car.city)); }} className="flex items-center gap-1 text-gray-400 hover:text-[#E5C158] truncate" data-testid={`car-card-city-${car.id}`}><MapPin className="w-3 h-3 text-[#D4AF37] shrink-0" />{car.city}</button>
          <span className="text-gray-400 truncate max-w-[50%]">{car.seller_name}</span>
        </div>
      </div>
    </Link>
  );
}
